import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { browserSupportsWebAuthn, loginWithPasskey, registerPasskey } from '@/lib/passkey';
import { detectTimeZone, localDateInTz } from '@/lib/tz';
import { BUILD_SHA, BUILT_AT } from '@/lib/version';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const qc = useQueryClient();
  const whoami = useQuery({ queryKey: ['whoami'], queryFn: api.whoami });
  const supported = browserSupportsWebAuthn();
  const loggedIn = !!whoami.data?.logged_in;
  const hasAnyPasskey = (whoami.data?.passkeys_registered ?? 0) > 0;
  const tz = React.useMemo(detectTimeZone, []);
  const today = React.useMemo(() => localDateInTz(new Date(), tz), [tz]);

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Passkeys, API keys, export, timezone.
        </p>
      </section>

      <AuthCard
        whoami={whoami.data}
        loggedIn={loggedIn}
        hasAnyPasskey={hasAnyPasskey}
        supported={supported}
        onChange={() => qc.invalidateQueries({ queryKey: ['whoami'] })}
      />

      {loggedIn && <PasskeysCard />}
      {loggedIn && <ApiKeysCard />}
      {loggedIn && <ExportCard />}

      <Card>
        <CardHeader>
          <CardTitle>Timezone</CardTitle>
          <CardDescription>
            Detected from your browser. All times stored in UTC; daily rollups keyed by this
            local date.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm">
          <div>
            <span className="text-muted-foreground">Timezone: </span>
            <code className="font-mono text-foreground">{tz}</code>
          </div>
          <div>
            <span className="text-muted-foreground">Local date today: </span>
            <code className="font-mono text-foreground">{today}</code>
          </div>
        </CardContent>
      </Card>

      <ForceRefreshCard />

      <p className="text-center text-[10px] uppercase tracking-wide text-muted-foreground">
        build <code className="font-mono">{BUILD_SHA}</code>
        {' · '}
        {new Date(BUILT_AT).toLocaleString([], {
          dateStyle: 'short',
          timeStyle: 'short',
        })}
      </p>
    </div>
  );
}

function ForceRefreshCard() {
  const [busy, setBusy] = React.useState(false);

  async function forceRefresh() {
    setBusy(true);
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } finally {
      // Reload bypassing the bfcache; service worker + caches are now gone,
      // so the next fetch goes to network for the freshest bundle.
      window.location.reload();
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Force refresh</CardTitle>
        <CardDescription>
          Unregister the service worker, clear all caches, and reload. Use this if a deploy
          isn't showing up.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="destructive"
          disabled={busy}
          onClick={() => {
            if (confirm('Unregister service worker, clear caches, and reload?')) {
              void forceRefresh();
            }
          }}
        >
          {busy ? 'Reloading…' : 'Force refresh'}
        </Button>
      </CardContent>
    </Card>
  );
}

function AuthCard({
  whoami,
  loggedIn,
  hasAnyPasskey,
  supported,
  onChange,
}: {
  whoami: { logged_in: boolean; passkeys_registered: number } | undefined;
  loggedIn: boolean;
  hasAnyPasskey: boolean;
  supported: boolean;
  onChange: () => void;
}) {
  const [deviceLabel, setDeviceLabel] = React.useState('');
  const [apiKeyToken, setApiKeyToken] = React.useState('');

  const register = useMutation({
    mutationFn: () => registerPasskey(deviceLabel || undefined),
    onSettled: onChange,
  });
  const login = useMutation({
    mutationFn: () => loginWithPasskey(),
    onSettled: onChange,
  });
  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSettled: onChange,
  });
  const apiKeyLogin = useMutation({
    mutationFn: (token: string) => api.apiKeyLogin(token),
    onSuccess: () => setApiKeyToken(''),
    onSettled: onChange,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Passkey sign-in</CardTitle>
        <CardDescription>
          {loggedIn
            ? 'You are signed in via session cookie.'
            : hasAnyPasskey
              ? 'Sign in with a registered passkey to use the app.'
              : 'No passkey registered yet. Register one to own this installation.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {!supported && (
          <p className="text-sm text-destructive">
            This browser does not support WebAuthn. Use a recent iOS Safari, Chrome, or Firefox.
          </p>
        )}

        {(!loggedIn || !hasAnyPasskey) && (
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-muted-foreground">
              Device label (optional)
              <input
                type="text"
                value={deviceLabel}
                onChange={(e) => setDeviceLabel(e.target.value)}
                placeholder="e.g. iPhone 15 Pro"
                className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                disabled={register.isPending}
              />
            </label>
            <Button
              disabled={!supported || register.isPending}
              onClick={() => register.mutate()}
            >
              {register.isPending ? 'Following prompts…' : 'Register a passkey'}
            </Button>
            {register.error && (
              <p className="text-sm text-destructive">{(register.error as Error).message}</p>
            )}
          </div>
        )}

        {!loggedIn && hasAnyPasskey && (
          <Button
            variant="outline"
            disabled={!supported || login.isPending}
            onClick={() => login.mutate()}
          >
            {login.isPending ? 'Following prompts…' : 'Sign in with passkey'}
          </Button>
        )}
        {login.error && (
          <p className="text-sm text-destructive">{(login.error as Error).message}</p>
        )}

        {loggedIn && (
          <Button
            variant="ghost"
            disabled={logout.isPending}
            onClick={() => logout.mutate()}
            className="self-start"
          >
            Log out
          </Button>
        )}

        {!loggedIn && (
          <details className="rounded-md border border-dashed border-border p-3 text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              Sign in with an API key (dev / LAN-IP fallback)
            </summary>
            <p className="mt-2 text-xs text-muted-foreground">
              Passkeys require localhost or HTTPS. To sign in over a bare
              LAN IP, mint a token on the host with{' '}
              <code className="font-mono">
                docker compose exec app deno task mint-api-key --label dev
              </code>{' '}
              and paste it below.
            </p>
            <div className="mt-2 flex gap-2">
              <input
                type="password"
                value={apiKeyToken}
                onChange={(e) => setApiKeyToken(e.target.value)}
                placeholder="etk_…"
                className="h-9 flex-1 rounded-md border border-input bg-background px-3 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                autoComplete="off"
              />
              <Button
                size="sm"
                disabled={apiKeyLogin.isPending || !apiKeyToken.trim()}
                onClick={() => apiKeyLogin.mutate(apiKeyToken.trim())}
              >
                {apiKeyLogin.isPending ? 'Signing in…' : 'Sign in'}
              </Button>
            </div>
            {apiKeyLogin.error && (
              <p className="mt-2 text-xs text-destructive">
                {(apiKeyLogin.error as Error).message}
              </p>
            )}
          </details>
        )}

        {whoami && (
          <p className="text-xs text-muted-foreground">
            Passkeys registered: {whoami.passkeys_registered}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PasskeysCard() {
  const qc = useQueryClient();
  const passkeys = useQuery({ queryKey: ['passkeys'], queryFn: api.listPasskeys });
  const [deviceLabel, setDeviceLabel] = React.useState('');
  const register = useMutation({
    mutationFn: () => registerPasskey(deviceLabel || undefined),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['passkeys'] });
      qc.invalidateQueries({ queryKey: ['whoami'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deletePasskey(id),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['passkeys'] });
      qc.invalidateQueries({ queryKey: ['whoami'] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Passkeys</CardTitle>
        <CardDescription>
          Add a passkey from each device you log in from. The last passkey can't be removed —
          register a new one first.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={deviceLabel}
            onChange={(e) => setDeviceLabel(e.target.value)}
            placeholder="Device label"
            className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <Button
            size="sm"
            onClick={() => register.mutate()}
            disabled={register.isPending}
          >
            {register.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>
        {register.error && (
          <p className="text-sm text-destructive">{(register.error as Error).message}</p>
        )}

        {passkeys.isPending && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {passkeys.data?.items.map((p) => (
          <div key={p.id} className="flex items-start gap-3 rounded-md border border-border p-3">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="font-medium">
                {p.device_label || 'Unlabeled device'}
              </span>
              <span className="text-xs text-muted-foreground">
                <code className="font-mono">{p.id_prefix}…</code> ·{' '}
                added {new Date(p.created_at).toLocaleDateString()} ·{' '}
                {p.last_used_at
                  ? `last used ${new Date(p.last_used_at).toLocaleDateString()}`
                  : 'never used'}
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (confirm('Remove this passkey?')) remove.mutate(p.id);
              }}
              disabled={remove.isPending}
              className="text-destructive"
            >
              Remove
            </Button>
          </div>
        ))}
        {remove.error && (
          <p className="text-sm text-destructive">{(remove.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

function ApiKeysCard() {
  const qc = useQueryClient();
  const keys = useQuery({ queryKey: ['api-keys'], queryFn: api.listApiKeys });
  const [label, setLabel] = React.useState('');
  const [mintedToken, setMintedToken] = React.useState<string | null>(null);
  const mint = useMutation({
    mutationFn: (l: string) => api.mintApiKey(l),
    onSuccess: (data) => {
      setMintedToken(data.token);
      setLabel('');
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeApiKey(id),
    onSettled: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>API keys</CardTitle>
        <CardDescription>
          Bearer tokens for Siri Shortcuts and other webhooks. The token is shown once at
          mint — copy it now. See the{' '}
          <Link to="/help/shortcuts" className="underline">
            Shortcuts setup guide
          </Link>{' '}
          for how to use them.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (e.g. iPhone Siri)"
            className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <Button
            size="sm"
            disabled={mint.isPending || label.trim().length === 0}
            onClick={() => mint.mutate(label.trim())}
          >
            {mint.isPending ? 'Minting…' : 'Mint'}
          </Button>
        </div>
        {mint.error && (
          <p className="text-sm text-destructive">{(mint.error as Error).message}</p>
        )}
        {mintedToken && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
              New token — copy it now, it won't be shown again:
            </p>
            <code className="mt-1 block break-all rounded bg-background p-2 font-mono text-xs">
              {mintedToken}
            </code>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                void navigator.clipboard.writeText(mintedToken).then(() => setMintedToken(null));
              }}
              className="mt-2"
            >
              Copy & dismiss
            </Button>
          </div>
        )}

        {keys.isPending && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {keys.data?.items.map((k) => (
          <div key={k.id} className="flex items-start gap-3 rounded-md border border-border p-3">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="font-medium">
                {k.label}
                {k.revoked_at && (
                  <span className="ml-2 text-xs text-destructive">revoked</span>
                )}
              </span>
              <span className="text-xs text-muted-foreground">
                Created {new Date(k.created_at).toLocaleDateString()} ·{' '}
                {k.last_used_at
                  ? `last used ${new Date(k.last_used_at).toLocaleString()}`
                  : 'never used'}
              </span>
            </div>
            {!k.revoked_at && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (confirm(`Revoke "${k.label}"?`)) revoke.mutate(k.id);
                }}
                disabled={revoke.isPending}
                className="text-destructive"
              >
                Revoke
              </Button>
            )}
          </div>
        ))}
        {revoke.error && (
          <p className="text-sm text-destructive">{(revoke.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

function ExportCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Export</CardTitle>
        <CardDescription>
          Full dump of incidents and rollups. JSON preserves types; CSV is spreadsheet-friendly.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <a href="/api/export/incidents.json" download>
              incidents.json
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href="/api/export/incidents.csv" download>
              incidents.csv
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href="/api/export/rollups.json" download>
              rollups.json
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href="/api/export/rollups.csv" download>
              rollups.csv
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
